import {PresentationPaneHeader} from "../../../components";
import {FiduciaryMember, FiduciarySummary, FiduciaryKey} from "../../../EstateFlowchart/model/EstateFlowchartTypes";
import {isLivingPartner} from "../../../EstateFlowchart/utils/FiduciaryUtils";
import {useAppSelector} from "../../../store/hooks";
import {familyMembersSelector} from "../../../EstateFlowchart/slices/EstateFlowChartSlice";
import {MemberGroupMember} from "../../../ClientManagement/models/InvestorGroupType";
import React from "react";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";
import {memberHasAnyFiduciaries} from "../../../EstateFlowchart/utils/DefUtils";

type ReviewFiduciariesProps = {
    fiduciarySummary: FiduciarySummary,
    isPrimaryContinued?: boolean,
    isPartnerContinued?: boolean
}

const renderFiduciarySection = (
    title: string,
    uniqueMemberOrders: number[],
    fiduciaryList: FiduciaryMember[] | undefined,
    memberGroups: MemberGroupMember[],
    enableEstateFlowchartMVPRelease?: boolean,
    continuedSections?: FiduciaryKey[]
) => {
    function getClassName() {
        const categoryTitle = title === "Executor" ? "executor" : "fiduciary-category-header";
        return enableEstateFlowchartMVPRelease ? "fiduciary-category-header" : categoryTitle;
    }

    // Only add (continued) if this section is in continuedSections
    const sectionKeyMap: Record<string, FiduciaryKey> = {
        "Executor": FiduciaryKey.Executors,
        "Guardian for Minors": FiduciaryKey.GuardiansForMinors,
        "Trustees": FiduciaryKey.Trustees,
        "Financial Power of Attorney": FiduciaryKey.FinancialPowerOfAttorneys,
        "Healthcare Power of Attorney": FiduciaryKey.HealthcarePowerOfAttorneys
    };
    const sectionKey = sectionKeyMap[title];
    const displayTitle = continuedSections?.includes(sectionKey)
        ? `${title} (continued)`
        : title;

    return (
        <>
            <div className={getClassName()}>{displayTitle}</div>
            <div className="fiduciary-member-list">
                {uniqueMemberOrders.map((memberOrder, index) => {
                    // Find the override value for this memberOrder group, if present
                    const groupMembers = fiduciaryList?.filter(item => item.memberOrder === memberOrder) ?? [];
                    // Use memberOrderOverride from the first member in the group if present, else fallback to index+1
                    const overrideNumber = groupMembers.length > 0 && groupMembers[0].memberOrderOverride
                        ? groupMembers[0].memberOrderOverride
                        : index + 1;

                    return enableEstateFlowchartMVPRelease ? (
                        <div className="fiduciary-member-list-item" key={memberOrder}>
                            <span className="fiduciary-member-list-number">{overrideNumber}.</span>
                            <span className="fiduciary-member-list-text">
                                {groupMembers.map((item, itemIndex) => {
                                    const selectedMember = memberGroups.find(
                                        member => member.id === item.memberId
                                    );
                                    const name = selectedMember?.firstName
                                        ? `${selectedMember.firstName} ${selectedMember.lastName}`
                                        : item.customName;
                                    return (
                                        <>
                                            {`${name}${
                                                itemIndex < groupMembers.length - 1
                                                    ? ", "
                                                    : ""
                                            }`}
                                        </>
                                    );
                                })}
                            </span>
                        </div>
                    ) : (
                        <div key={memberOrder}>
                            {overrideNumber}. {groupMembers.map((item, itemIndex) => {
                                const selectedMember = memberGroups.find(
                                    member => member.id === item.memberId
                                );
                                return (
                                    <>
                                        {`${selectedMember?.firstName} ${selectedMember?.lastName}${
                                            itemIndex < groupMembers.length - 1
                                                ? ", "
                                                : ""
                                        }`}
                                    </>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

const renderExecutorSection = (
    uniqueMemberOrders: number[],
    fiduciaryList: FiduciaryMember[] | undefined,
    memberGroups: MemberGroupMember[],
    enableEstateFlowchartMVPRelease?: boolean,
    continuedSections?: FiduciaryKey[]
) => renderFiduciarySection("Executor", uniqueMemberOrders, fiduciaryList, memberGroups, enableEstateFlowchartMVPRelease, continuedSections);

const renderGuardianSection = (
    uniqueMemberOrders: number[],
    fiduciaryList: FiduciaryMember[] | undefined,
    memberGroups: MemberGroupMember[],
    enableEstateFlowchartMVPRelease?: boolean,
    continuedSections?: FiduciaryKey[]
) => renderFiduciarySection("Guardian for Minors", uniqueMemberOrders, fiduciaryList, memberGroups, enableEstateFlowchartMVPRelease, continuedSections);

const renderTrusteeSection = (
    uniqueMemberOrders: number[],
    fiduciaryList: FiduciaryMember[] | undefined,
    memberGroups: MemberGroupMember[],
    enableEstateFlowchartMVPRelease?: boolean,
    continuedSections?: FiduciaryKey[]
) => renderFiduciarySection("Trustees", uniqueMemberOrders, fiduciaryList, memberGroups, enableEstateFlowchartMVPRelease, continuedSections);

const renderFinancialPowerOfAttorneySection = (
    uniqueMemberOrders: number[],
    fiduciaryList: FiduciaryMember[] | undefined,
    memberGroups: MemberGroupMember[],
    enableEstateFlowchartMVPRelease?: boolean,
    continuedSections?: FiduciaryKey[]
) => renderFiduciarySection("Financial Power of Attorney", uniqueMemberOrders, fiduciaryList, memberGroups, enableEstateFlowchartMVPRelease, continuedSections);

const renderHealthcarePowerOfAttorneySection = (
    uniqueMemberOrders: number[],
    fiduciaryList: FiduciaryMember[] | undefined,
    memberGroups: MemberGroupMember[],
    enableEstateFlowchartMVPRelease?: boolean,
    continuedSections?: FiduciaryKey[]
) => renderFiduciarySection("Healthcare Power of Attorney", uniqueMemberOrders, fiduciaryList, memberGroups, enableEstateFlowchartMVPRelease, continuedSections);

const renderNoFiduciariesText = (isContinued: boolean = false) => (
    <div className="no-fiduciaries-text">
        {isContinued ? "No additional fiduciaries" : "No fiduciaries"}
    </div>
);

const ReviewFiduciaries = ({
    fiduciarySummary,
    isPrimaryContinued = false,
    isPartnerContinued = false
}: ReviewFiduciariesProps) => {

    const familyMembers = useAppSelector(familyMembersSelector);
    const enableEstateFlowchartMVPRelease = useAppSelector(selectReleaseToggles)?.enableEstateFlowchartMVPRelease;

    const memberGroups: MemberGroupMember[] = []
    if (familyMembers) {
        memberGroups.push(familyMembers.primaryMember);
        familyMembers.partnerMember && memberGroups.push(familyMembers.partnerMember);
        memberGroups.push(...(familyMembers.additionalMembers ?? []));
    }


    const uniqueMemberOrders = (data: FiduciaryMember[]) => {
        let executorsMemberOrders: number[] = data.sort((a, b) => a.memberOrder! - b.memberOrder!).map(ele => {
            return ele.memberOrder!
        })

        return executorsMemberOrders.filter((item, index) => executorsMemberOrders.indexOf(item) === index);
    };

    const getUniqueMemberOrders = (memberType?: FiduciaryMember[]) =>
        memberType ? uniqueMemberOrders(memberType) : [];

    const uniqueExecutorsMemberOrdersPrimaryMember = getUniqueMemberOrders(fiduciarySummary.primaryMember?.executors);
    const uniqueGuardiansForMinorsMemberOrdersPrimaryMember = getUniqueMemberOrders(fiduciarySummary.primaryMember?.guardiansForMinors);
    const uniqueTrusteesMemberOrdersPrimaryMember = getUniqueMemberOrders(fiduciarySummary.primaryMember?.trustees);
    const uniqueFinancialPowerOfAttorneysMemberOrdersPrimaryMember = getUniqueMemberOrders(fiduciarySummary.primaryMember?.financialPowerOfAttorneys);
    const uniqueHealthcarePowerOfAttorneysMemberOrdersPrimaryMember = getUniqueMemberOrders(fiduciarySummary.primaryMember?.healthcarePowerOfAttorneys);

    const uniqueExecutorsMemberOrdersPartnerMember = getUniqueMemberOrders(fiduciarySummary.partnerMember?.executors);
    const uniqueGuardiansForMinorsMemberOrdersPartnerMember = getUniqueMemberOrders(fiduciarySummary.partnerMember?.guardiansForMinors);
    const uniqueTrusteesMemberOrdersPartnerMember = getUniqueMemberOrders(fiduciarySummary.partnerMember?.trustees);
    const uniqueFinancialPowerOfAttorneysMemberOrdersPartnerMember = getUniqueMemberOrders(fiduciarySummary.partnerMember?.financialPowerOfAttorneys);
    const uniqueHealthcarePowerOfAttorneysMemberOrdersPartnerMember = getUniqueMemberOrders(fiduciarySummary.partnerMember?.healthcarePowerOfAttorneys);

    // Determine if either member is continued for header
    const isAnyContinued = isPrimaryContinued || isPartnerContinued;

    return (
        <>
            <PresentationPaneHeader
                displayName={"ESTATE FLOWCHART"}
                title={`Review Fiduciaries${isAnyContinued ? " (continued)" : ""}`}
                callingFromDEFReport={true}
            />
            <div
                className={
                    enableEstateFlowchartMVPRelease
                        ? 'enable-mvp-release-review-fiduciaries-report'
                        : 'review-fiduciaries-report-page'
                }
            >
                {familyMembers?.primaryMember && (
                    <div className="fiduciary-column-first" data-testid={"fiduciary-column-primary"}>
                        <h4>
                            {familyMembers.primaryMember?.firstName} {familyMembers?.primaryMember?.lastName}
                        </h4>
                        {!memberHasAnyFiduciaries(fiduciarySummary.primaryMember) ? (
                            renderNoFiduciariesText(isPrimaryContinued)
                        ) : (
                            <>
                                {uniqueExecutorsMemberOrdersPrimaryMember.length > 0 &&
                                    renderExecutorSection(
                                        uniqueExecutorsMemberOrdersPrimaryMember,
                                        fiduciarySummary.primaryMember?.executors,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.primaryMember?.continuedSections
                                    )}
                                {uniqueGuardiansForMinorsMemberOrdersPrimaryMember.length > 0 &&
                                    renderGuardianSection(
                                        uniqueGuardiansForMinorsMemberOrdersPrimaryMember,
                                        fiduciarySummary.primaryMember?.guardiansForMinors,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.primaryMember?.continuedSections
                                    )}
                                {uniqueTrusteesMemberOrdersPrimaryMember.length > 0 &&
                                    renderTrusteeSection(
                                        uniqueTrusteesMemberOrdersPrimaryMember,
                                        fiduciarySummary.primaryMember?.trustees,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.primaryMember?.continuedSections
                                    )}
                                {uniqueFinancialPowerOfAttorneysMemberOrdersPrimaryMember.length > 0 &&
                                    renderFinancialPowerOfAttorneySection(
                                        uniqueFinancialPowerOfAttorneysMemberOrdersPrimaryMember,
                                        fiduciarySummary.primaryMember?.financialPowerOfAttorneys,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.primaryMember?.continuedSections
                                    )}
                                {uniqueHealthcarePowerOfAttorneysMemberOrdersPrimaryMember.length > 0 &&
                                    renderHealthcarePowerOfAttorneySection(
                                        uniqueHealthcarePowerOfAttorneysMemberOrdersPrimaryMember,
                                        fiduciarySummary.primaryMember?.healthcarePowerOfAttorneys,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.primaryMember?.continuedSections
                                    )}
                            </>
                        )}
                    </div>
                )}
                {isLivingPartner(familyMembers) && familyMembers?.partnerMember && (
                    <div className="fiduciary-column-second" data-testid={"fiduciary-column-partner"}>
                        <h4>
                            {familyMembers.partnerMember.firstName} {familyMembers.partnerMember.lastName}
                        </h4>
                        {!memberHasAnyFiduciaries(fiduciarySummary.partnerMember) ? (
                            renderNoFiduciariesText(isPartnerContinued)
                        ) : (
                            <>
                                {uniqueExecutorsMemberOrdersPartnerMember.length > 0 &&
                                    renderExecutorSection(
                                        uniqueExecutorsMemberOrdersPartnerMember,
                                        fiduciarySummary.partnerMember?.executors,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.partnerMember?.continuedSections
                                    )}
                                {uniqueGuardiansForMinorsMemberOrdersPartnerMember.length > 0 &&
                                    renderGuardianSection(
                                        uniqueGuardiansForMinorsMemberOrdersPartnerMember,
                                        fiduciarySummary.partnerMember?.guardiansForMinors,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.partnerMember?.continuedSections
                                    )}
                                {uniqueTrusteesMemberOrdersPartnerMember.length > 0 &&
                                    renderTrusteeSection(
                                        uniqueTrusteesMemberOrdersPartnerMember,
                                        fiduciarySummary.partnerMember?.trustees,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.partnerMember?.continuedSections
                                    )}
                                {uniqueFinancialPowerOfAttorneysMemberOrdersPartnerMember.length > 0 &&
                                    renderFinancialPowerOfAttorneySection(
                                        uniqueFinancialPowerOfAttorneysMemberOrdersPartnerMember,
                                        fiduciarySummary.partnerMember?.financialPowerOfAttorneys,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.partnerMember?.continuedSections
                                    )}
                                {uniqueHealthcarePowerOfAttorneysMemberOrdersPartnerMember.length > 0 &&
                                    renderHealthcarePowerOfAttorneySection(
                                        uniqueHealthcarePowerOfAttorneysMemberOrdersPartnerMember,
                                        fiduciarySummary.partnerMember?.healthcarePowerOfAttorneys,
                                        memberGroups,
                                        enableEstateFlowchartMVPRelease,
                                        fiduciarySummary.partnerMember?.continuedSections
                                    )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default ReviewFiduciaries;

